import MDBox from "components/MDBox";
import { useEffect, useState, useRef, useMemo } from "react";
import {
  addSuccessionPlanStep,
  deleteSuccessionPlanStep,
  updateSuccessionPlanStep,
  addSuccessionPlanStepTraining,
  deleteStepTraining,
  getTrainings,
} from "services/api";
import {
  Box,
  Typography,
  Chip,
  Paper,
  Divider,
  Button,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  TextField,
  CircularProgress,
  Stack,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // Added missing importimport { useLocation } from "react-router-dom";
import { useLocation } from "react-router-dom";
import EmployeeAvatar from "components/EmployeeAvatar";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoadingScreen from "components/Loading";
import CustomStepIcon from "./CustomStep";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";

const SuccessionSteps = () => {
  const { state } = useLocation();
  const { successor, job, jobs } = state || {};

  const snackbarRef = useRef();

  const { t } = useTranslation();
  const jobPurposeRaw = job?.jobPurpose ?? "No job purpose available";
  const plainText = jobPurposeRaw.replace(/<[^>]*>/g, "");
  const isLongText = plainText.length > 200;

  const navigate = useNavigate();
  const [successionSteps, setSuccessionSteps] = useState(() => {
    if (successor?.steps) {
      return [...successor.steps].sort((a, b) => new Date(a.planDate) - new Date(b.planDate));
    }
    return [];
  });

  const [loading, setLoading] = useState(false);
  const [isStepFormOpen, setIsStepFormOpen] = useState(false); //show/hide form for adding and editing steps
  const [newStepJobId, setNewStepJobId] = useState(null); //selected job (add mode)
  const [newStepDate, setNewStepDate] = useState(""); // selected date (add mode)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteStepId, setDeleteStepId] = useState(null); //which step to delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [savingLoading, setSavingLoading] = useState(false);
  const [isEditingStep, setIsEditingStep] = useState(false); //are we editing?
  const [editingStepId, setEditingStepId] = useState(null); //which step is being edited
  const [editDraft, setEditDraft] = useState(null);
  const [stepIdForShownTrainings, setStepIdForShownTrainings] = useState(null);
  const [confirmTrainingOpen, setConfirmTrainingOpen] = useState(false); //for deleting training
  const [deleteTrainingId, setDeleteTrainingId] = useState(null);
  const [allTrainings, setAllTrainings] = useState([]);
  const [newTrainingId, setNewTrainingId] = useState(null); // Selected training ID
  const [isTrainingFormOpen, setIsTrainingFormOpen] = useState(false); // for adding training
  const [savingTrainingLoading, setSavingTrainingLoading] = useState(false);

  const handleOpenJobDetails = () => {
    navigate("/job-details", {
      state: {
        jobId: job?.jobId,
        fromSuccessors: true,
      },
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchAllTrainings = async () => {
      try {
        const data = await getTrainings(); // Your API function
        if (data !== "error") {
          setAllTrainings(data);
        }
      } catch (error) {
        console.error("Error fetching training list:", error);
      }
    };
    fetchAllTrainings();
  }, []);

  const filteredJobs = useMemo(() => {
    // 1. Get IDs of jobs used by OTHER steps (not the one we are editing)
    const usedByOtherSteps =
      successionSteps
        ?.filter((step) => step.id !== editingStepId) //Ignore the step we are currently editing
        .map((step) => step.outcomeJobId) || [];

    return (jobs || []).filter((job) => {
      // 2. A job is available if it's not used by any OTHER step
      return !usedByOtherSteps.includes(job.jobId);
    });
    // Re-run if we switch steps or update steps
  }, [jobs, successionSteps, editingStepId]);

  const handleSaveTraining = async () => {
    if (!newTrainingId) {
      snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.selectTraining"), "error");
      return;
    }

    const payload = {
      stepId: stepIdForShownTrainings,
      trainingId: newTrainingId,
    };

    setSavingTrainingLoading(true);

    try {
      const result = await addSuccessionPlanStepTraining(payload);

      if (result !== "error") {
        // Find the name of the training to update the UI immediately
        const trainingName = allTrainings.find((t) => t.id === newTrainingId)?.trainingName || "";

        // Update the nested trainings array in successionSteps
        setSuccessionSteps((prev) =>
          prev.map((step) =>
            step.id === stepIdForShownTrainings
              ? { ...step, trainings: [...(step.trainings || []), { ...result, trainingName }] }
              : step
          )
        );

        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.trainingAdded"), "success");

        // Reset Form
        setIsTrainingFormOpen(false);
        setNewTrainingId(null);
      } else {
        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.trainingFailed"), "error");
      }
    } catch (error) {
      console.error("Error saving training:", error);
      snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.trainingFailed"), "error");
    } finally {
      setSavingTrainingLoading(false);
    }
  };

  const handleEditStep = (step) => {
    setIsStepFormOpen(true);
    setIsEditingStep(true);
    setEditingStepId(step.id);
    setEditDraft({ ...step });
  };

  const handleSaveStep = async () => {
    // 1. Validation: Don't allow saving if fields are empty
    if (!isEditingStep && (!newStepJobId || !newStepDate)) {
      snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.missingFields"), "error");
      return;
    }

    const payload = {
      successorId: successor?.id,
      outcomeJobId: isEditingStep ? editDraft?.outcomeJobId : newStepJobId,
      planDate: isEditingStep ? editDraft?.planDate : newStepDate,
    };

    setSavingLoading(true);

    try {
      let result;

      if (isEditingStep) {
        result = await updateSuccessionPlanStep(editingStepId, payload);
      } else {
        result = await addSuccessionPlanStep(payload);
      }

      if (result !== "error") {
        // Modify succession steps
        const jobName = jobs?.find((j) => j.jobId === result.outcomeJobId)?.jobName || "";

        if (isEditingStep) {
          // UPDATE existing step in list
          setSuccessionSteps((prev) =>
            prev
              .map((step) => (step.id === editingStepId ? { ...result, jobName } : step))
              .sort((a, b) => new Date(a.planDate) - new Date(b.planDate))
          );

          snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepUpdated"), "success");
        } else {
          // ADD new step
          setSuccessionSteps((prev) =>
            [...prev, { ...result, jobName }].sort(
              (a, b) => new Date(a.planDate) - new Date(b.planDate)
            )
          );

          snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepAdded"), "success");
        }

        handleClearStep();

        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepAdded"), "success");
      } else {
        // Handle API error
        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepFailed"), "error");
      }
    } catch (error) {
      console.error("Error saving step:", error);
      snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepFailed"), "error");
    } finally {
      setSavingLoading(false); // stop loading
    }
  };

  const handleDeleteStep = async (id) => {
    setDeleteLoading(true);

    try {
      const result = await deleteSuccessionPlanStep(id);

      if (result !== "error") {
        // ✅ remove from state
        setSuccessionSteps((prev) => prev.filter((step) => step.id !== id));

        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepDeleted"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepDeleteFailed"), "error");
      }
    } catch (error) {
      console.error("Error deleting step:", error);

      snackbarRef.current?.showSnackbar(t("admin.successionPlanSteps.stepDeleteFailed"), "error");
    } finally {
      setDeleteLoading(false); // ✅ unlock after done
    }
  };

  const handleDeleteTraining = async (id) => {
    setDeleteLoading(true);

    try {
      // Replace with your actual API call for deleting training
      const result = await deleteStepTraining(id);

      if (result !== "error") {
        // ✅ Remove the training from the specific step in state
        setSuccessionSteps((prev) =>
          prev.map((step) => ({
            ...step,
            trainings: step.trainings.filter((t) => t.id !== id),
          }))
        );

        snackbarRef.current?.showSnackbar(
          t("admin.successionPlanSteps.trainingDeleted"),
          "success"
        );
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.successionPlanSteps.trainingDeletedFailed"),
          "error"
        );
      }
    } catch (error) {
      console.error("Error deleting training:", error);
      snackbarRef.current?.showSnackbar(
        t("admin.successionPlanSteps.trainingDeletedFailed"),
        "error"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClearStep = () => {
    setIsStepFormOpen(false);
    setNewStepJobId(null); // Reset job selection
    setNewStepDate(""); // Reset date (empty string for date inputs)
    setIsEditingStep(false);
    setEditingStepId(null);
  };

  const handleOpenConfirm = (id) => {
    setDeleteStepId(id);
    setConfirmOpen(true);
  };

  const handleOpenConfirmTrainingDelete = (id) => {
    setDeleteTrainingId(id);
    setConfirmTrainingOpen(true);
  };

  const activeStepIndex = useMemo(() => {
    const today = new Date().setHours(0, 0, 0, 0);
    // Find the first step that is today or in the future
    const index = successionSteps.findIndex((s) => new Date(s.planDate) >= today);
    return index === -1 ? successionSteps.length : index; // If all dates are in the past, return the length (all completed)
  }, [successionSteps]);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      {/* Back Button */}
      <Button
        onClick={handleBack}
        startIcon={<ArrowBackIcon />}
        sx={{
          mb: 1,
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        {t("common.back")}
      </Button>

      <Box
        sx={{ display: "flex", gap: 4, width: "100%", flexDirection: { xs: "column", md: "row" } }}
      >
        <Box
          sx={{
            p: 4,
            display: "flex",
            gap: 4,
            bgcolor: "#fcfcfc",
            width: "100%",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Sidebar */} {/* Sidebar Container */}
          <Box
            sx={{
              flex: 1,
              maxWidth: { md: "35%" }, // optional constraint, still not fixed px
            }}
          >
            {/* Top Profile Paper */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: "8px", mb: 3 }}>
              <EmployeeAvatar
                photo={successor?.photo}
                sx={{ width: 70, height: 70, mb: 2 }}
                boxSx={{
                  justifyContent: "flex-start",
                }}
              />{" "}
              <Typography variant="h5" fontWeight="700">
                {successor?.employeeName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
                  {t("admin.successionPlanSteps.successorJob")}:
                </Typography>{" "}
                {job?.jobName}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 3 }}>
                {t("admin.successionPlanSteps.currentProg")}
              </Typography>
              {/* MUI Stepper for the vertical line and steps */}
              <Stepper
                orientation="vertical"
                connector={<Box sx={{ ml: "13px", borderLeft: "2px solid #eee", height: 30 }} />}
              >
                {successionSteps.map((step, index) => {
                  const isCompleted = index < activeStepIndex;
                  const isActive = index === activeStepIndex;
                  const isLongJobName = isSmallScreen
                    ? (step.jobName || "").length > 20
                    : (step.jobName || "").length > 35;
                  return (
                    <Step key={step.id} completed={isCompleted} active={isActive}>
                      <StepLabel
                        StepIconComponent={CustomStepIcon}
                        sx={{
                          "& .MuiStepLabel-label": {
                            textTransform: "none",
                          },
                        }}
                      >
                        <Box sx={{ ml: 1 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            display="block"
                            sx={{
                              fontWeight: "bold",
                              color: "#000000",
                              textTransform: "none",
                              fontWeight: 500,
                            }}
                          >
                            {t("admin.successionPlanSteps.step")} {index + 1}
                          </Typography>
                          <Tooltip
                            title={isLongJobName ? step.jobName || "" : ""}
                            arrow
                            placement="top"
                            disableHoverListener={!isLongJobName}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                lineHeight: 1.1,
                                color: "#ECEFF1",
                                maxWidth: {
                                  xs: "150px",
                                  md: "220px",
                                },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: isLongJobName ? "help" : "default",
                              }}
                            >
                              {step.jobName}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Paper>

            {/* Bottom About Role Paper */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="subtitle2" fontWeight="700" gutterBottom>
                {t("admin.successionPlanSteps.about")}
              </Typography>
              <Typography
                variant="body1"
                fontWeight="300"
                sx={{
                  mt: 1,
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                }}
              >
                {isLongText ? plainText.slice(0, 200) + "..." : plainText}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<InfoOutlinedIcon />}
                onClick={handleOpenJobDetails}
                sx={{
                  mt: 1,
                  textTransform: "none",
                  borderRadius: 2,
                  color: "text.primary",
                  borderColor: "#e0e0e0",
                  fontWeight: "600",
                }}
              >
                {t("admin.jobs.jobDialog.jobDetails")}
              </Button>
            </Paper>

            {/* Righttt Sideeee */}
          </Box>
          <Box sx={{ flex: 2 }}>
            <Box mb={3}>
              <Typography variant="h5" fontWeight="700">
                {t("admin.successionPlanSteps.editSteps")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("admin.successionPlanSteps.editStepsDescp")}
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: "8px", mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="700">
                {t("admin.successionPlanSteps.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {t("admin.successionPlanSteps.modifySteps")}
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                {successionSteps.map((step, index) => {
                  const isExpanded = stepIdForShownTrainings === step.id;
                  const isLongJobName = isSmallScreen
                    ? (step.jobName || "").length > 10
                    : (step.jobName || "").length > 35;
                  return (
                    <Box
                      key={step.id}
                      // sx={{
                      //   display: "flex",
                      //   alignItems: "center",
                      //   p: 2,
                      //   borderRadius: "12px",
                      //   border: "1px solid #f0f0f0",
                      //   bgcolor: "white",
                      // }}
                      sx={{
                        display: "flex",
                        // Change direction: column on mobile, row on tablet+
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        p: 2,
                        gap: { xs: 2, sm: 0 }, // Add gap when items stack
                        borderRadius: "12px",
                        border: "1px solid #f0f0f0",
                        bgcolor: "white",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", flex: 1, width: "100%" }}>
                        <SchoolIcon sx={{ color: "#7b809a", mr: 2, fontSize: "1.1rem" }} />

                        <Box mr={2}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f0f0f0",
                              color: "#999",
                              fontWeight: "bold",
                            }}
                          >
                            {index + 1}
                          </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Tooltip
                            title={isLongJobName ? step.jobName || "" : ""}
                            arrow
                            placement="top"
                            disableHoverListener={!isLongJobName}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="700"
                              noWrap
                              sx={{
                                width: "100%",
                                maxWidth: {
                                  xs: "180px",
                                  md: "300px",
                                },
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                                cursor: isLongJobName ? "help" : "default",
                              }}
                            >
                              {step.jobName}
                            </Typography>
                          </Tooltip>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.85rem" }}
                          >
                            {t("admin.successionPlanSteps.targetDate")}: {step.planDate}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Status Chip */}
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          width: { xs: "100%", sm: "auto" },
                          justifyContent: { xs: "flex-end", sm: "flex-start" },
                          pt: { xs: 1, sm: 0 },
                          borderTop: { xs: "1px solid #f9f9f9", sm: "none" }, // Visual separator on mobile
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleEditStep(step)}
                          sx={{ border: "1px solid #eee", borderRadius: "8px" }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={deleteLoading}
                          onClick={() => handleOpenConfirm(step.id)}
                          sx={{ border: "1px solid #eee", borderRadius: "8px", color: "#ef4444" }}
                        >
                          {deleteLoading ? (
                            <CircularProgress size={16} sx={{ color: "black" }} />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>

                        {/* VIEW TRAININGS ICON (Beside Delete) */}
                        <Tooltip
                          title={
                            isExpanded
                              ? t("admin.successionPlanSteps.hideTrainings")
                              : t("admin.successionPlanSteps.viewTrainings")
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => setStepIdForShownTrainings(isExpanded ? null : step.id)}
                            sx={{
                              border: "1px solid #eee",
                              borderRadius: "8px",
                              color: isExpanded ? "#2563eb" : "inherit",
                              bgcolor: isExpanded ? "#f0f7ff" : "transparent",
                            }}
                          >
                            <MenuBookOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
                {isStepFormOpen && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: "2px solid #2563eb", // Blue border to show it's active
                      bgcolor: "#f8faff",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="700" color="primary">
                      {isEditingStep
                        ? t("admin.successionPlanSteps.modifyStep")
                        : t("admin.successionPlanSteps.newStep")}
                    </Typography>

                    <Box
                      display="flex"
                      gap={2}
                      alignItems="flex-start"
                      sx={{
                        display: "flex",
                        gap: 2,
                        flexDirection: { xs: "column", sm: "row" }, // Stack inputs on mobile
                        alignItems: "stretch",
                      }}
                    >
                      {/* Job Selection */}

                      <Autocomplete
                        sx={{ flex: 1 }}
                        options={filteredJobs}
                        value={
                          isEditingStep
                            ? filteredJobs?.find((j) => j.jobId === editDraft?.outcomeJobId) || null
                            : filteredJobs?.find((j) => j.jobId === newStepJobId) || null
                        }
                        isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
                        getOptionLabel={(option) => option.jobName || ""}
                        onChange={(_, newValue) => {
                          const id = newValue?.jobId || null;

                          if (isEditingStep) {
                            setEditDraft((prev) => ({ ...prev, outcomeJobId: id }));
                          } else {
                            setNewStepJobId(id);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("admin.successionPlanSteps.selectOutcomeJob")}
                            size="small"
                          />
                        )}
                      />

                      {/* Date Selection */}
                      <TextField
                        label={t("admin.successionPlanSteps.targetDate")}
                        type="date"
                        size="small"
                        sx={{ width: { xs: "100%", sm: 220 } }} // Full width on mobile
                        value={isEditingStep ? editDraft?.planDate || "" : newStepDate}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (isEditingStep) {
                            setEditDraft((prev) => ({ ...prev, planDate: value }));
                          } else {
                            setNewStepDate(value);
                          }
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>

                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Button size="small" onClick={handleClearStep} color="secondary">
                        {t("common.cancel")}
                      </Button>
                      <Button
                        size="small"
                        onClick={handleSaveStep}
                        disabled={savingLoading}
                        sx={{
                          "&:hover": { backgroundColor: "blue", color: "#fff" },
                        }}
                      >
                        {savingLoading ? (
                          <CircularProgress size={16} />
                        ) : isEditingStep ? (
                          t("common.update")
                        ) : (
                          t("common.save")
                        )}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Add  Step Button */}
              {!isStepFormOpen && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setIsStepFormOpen(true)} // Open the form
                  sx={{
                    mt: 3,
                    py: 1.5,
                    textTransform: "none",
                    borderRadius: "12px",
                    borderStyle: "dashed",
                    borderWidth: "2px",
                    color: "#2563eb",
                    fontWeight: "600",
                    "&:hover": { borderWidth: "2px", borderStyle: "dashed" },
                  }}
                >
                  {t("admin.successionPlanSteps.addStep")}
                </Button>
              )}
            </Paper>
            {stepIdForShownTrainings && (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: "8px", mb: 3 }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    borderRadius: "12px",
                    border: "1px solid #f0f0f0",
                    bgcolor: "white",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="700">
                      {t("admin.successionPlanSteps.stepTrainings")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {/* Find the selected step's name to show in the header */}
                      {successionSteps.find((s) => s.id === stepIdForShownTrainings)?.jobName}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setStepIdForShownTrainings(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Stack spacing={2}>
                  {successionSteps.find((s) => s.id === stepIdForShownTrainings)?.trainings
                    ?.length > 0 ? (
                    successionSteps
                      .find((s) => s.id === stepIdForShownTrainings)
                      .trainings.map((tr) => (
                        <Box
                          key={tr.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 2,
                            bgcolor: "#f9fafb",
                            borderRadius: "8px",
                            border: "1px solid #eee",
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight="700">
                              {tr.trainingName}
                            </Typography>
                            <Stack spacing={0.5} sx={{ pl: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                {t("admin.successionPlanSteps.startDate")}:{" "}
                                {tr.startDate?.split("T")[0]}
                              </Typography>

                              <Typography variant="caption" color="text.secondary">
                                {t("admin.successionPlanSteps.endDate")}:{" "}
                                {tr.endDate?.split("T")[0]}
                              </Typography>
                            </Stack>
                          </Box>

                          {!tr.finished && (
                            <IconButton
                              size="small"
                              disabled={deleteLoading}
                              onClick={() => handleOpenConfirmTrainingDelete(tr.id)}
                              sx={{
                                border: "1px solid #eee",
                                borderRadius: "8px",
                                color: "#ef4444",
                                bgcolor: "white",
                                "&:hover": { bgcolor: "#fff5f5" },
                              }}
                            >
                              {deleteLoading ? (
                                <CircularProgress size={16} sx={{ color: "black" }} />
                              ) : (
                                <DeleteOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          )}
                        </Box>
                      ))
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", color: "text.secondary" }}
                    >
                      {t("admin.successionPlanSteps.noTrainings")}
                    </Typography>
                  )}
                  {isTrainingFormOpen && (
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: "12px",
                        border: "2px solid #2563eb",
                        bgcolor: "#f8faff",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="700" color="primary">
                        {t("admin.successionPlanSteps.addTraining")}
                      </Typography>

                      <Autocomplete
                        options={allTrainings || []}
                        getOptionLabel={(option) => option.trainingName || ""}
                        value={allTrainings.find((t) => t.id === newTrainingId) || null}
                        onChange={(_, newValue) => setNewTrainingId(newValue?.id || null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("admin.successionPlanSteps.selectTr")}
                            size="small"
                            fullWidth
                          />
                        )}
                      />

                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button
                          size="small"
                          onClick={() => {
                            setIsTrainingFormOpen(false);
                            setNewTrainingId(null);
                          }}
                          color="secondary"
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button
                          size="small"
                          onClick={handleSaveTraining}
                          disabled={savingTrainingLoading}
                          sx={{
                            "&:hover": { backgroundColor: "blue", color: "#fff" },
                          }}
                        >
                          {savingTrainingLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            t("common.save")
                          )}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* --- ADD TRAINING BUTTON (Dashed style) --- */}
                  {!isTrainingFormOpen && (
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setIsTrainingFormOpen(true)}
                      sx={{
                        mt: 3,
                        py: 1,
                        textTransform: "none",
                        borderRadius: "12px",
                        borderStyle: "dashed",
                        borderWidth: "2px",
                        color: "#2563eb",
                        fontWeight: "600",
                        "&:hover": { borderWidth: "2px", borderStyle: "dashed" },
                      }}
                    >
                      {t("admin.successionPlanSteps.addTraining")}
                    </Button>
                  )}
                </Stack>
              </Paper>
            )}
          </Box>
          <ConfirmationDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={async () => {
              setConfirmOpen(false);

              if (deleteStepId) {
                await handleDeleteStep(deleteStepId);
                setDeleteStepId(null); // optional cleanup
              }
            }}
          />
          <ConfirmationDialog
            open={confirmTrainingOpen}
            onClose={() => setConfirmTrainingOpen(false)}
            onConfirm={async () => {
              setConfirmTrainingOpen(false);
              if (deleteTrainingId) {
                await handleDeleteTraining(deleteTrainingId);
                setDeleteTrainingId(null);
              }
            }}
          />
        </Box>
        <SnackbarWrapper
          ref={snackbarRef}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
        {/* Main Content Area */}
        <Box sx={{ flex: 1 }}>{/* Your right side content goes here */}</Box>
      </Box>
    </>
  );
};
export default SuccessionSteps;
